import { Undo20Px } from "@locaisolutions/icons";
import { Button } from "@mui/material";
import { ConfirmationModal, mainColours, useToast } from "@qubit/autoparts";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { fetchPortStatus, NextEmptyBinSuccessAction } from "~/redux/actions";
import {
  selectWorkstationAutostoreGridId,
  selectWorkstationId
} from "~/redux/selectors/workstationsSelectors";
import {
  useNextEmptyBinMutation,
  usePostGetPreviousBinMutation
} from "~/redux/warehouse/autostoreGrid.hooks";

import {
  selectIsPreviousBinFlow,
  selectPreviousPutawayTask,
  setPreviousBinNumber
} from "./autostorePutaway.slice";

export function GetPreviousBinButton() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { errorToast } = useToast();

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isLoadingPreviousBin, setIsLoadingPreviousBin] = useState(false);

  const autostoreGridId = useAppSelector(selectWorkstationAutostoreGridId);
  const workstationId = useAppSelector(selectWorkstationId);
  const isPreviousBinFlow = useAppSelector(selectIsPreviousBinFlow);
  const previousPutawayTask = useAppSelector(selectPreviousPutawayTask);

  const isButtonDisabled = !previousPutawayTask || isPreviousBinFlow;

  const [postGetPreviousBin] = usePostGetPreviousBinMutation();
  const [getNextEmptyBin] = useNextEmptyBinMutation();

  const handleGetPreviousBin = async () => {
    if (!autostoreGridId || !workstationId || !previousPutawayTask) {
      return errorToast(t("error getting previous bin"));
    }
    try {
      const { binId, portId } = previousPutawayTask;
      dispatch(setPreviousBinNumber(binId));

      await postGetPreviousBin({
        autostoreGridId,
        portId,
        binNumber: binId
      }).unwrap();

      const nextEmptyBin = await getNextEmptyBin({
        autostoreGridId,
        portId
      }).unwrap();
      dispatch<NextEmptyBinSuccessAction>({
        type: "autostore/NEXT_EMPTY_BIN_SUCCESS",
        payload: {
          nextEmptyBin,
          portId
        }
      });

      await dispatch(fetchPortStatus({ portId }));
    } catch (e) {
      errorToast(getMessageFromRtkError(e));
    }
  };

  const onConfirmGetPreviousBin = async () => {
    setIsLoadingPreviousBin(true);
    await handleGetPreviousBin();
    setIsConfirmationModalOpen(false);
    setIsLoadingPreviousBin(false);
  };

  const confirmationModalText = (
    <span>
      {t("are you sure you want to get the previous bin")}
      <br />
      <br />
      {t("get previous bin modal note")}
    </span>
  );

  return (
    <>
      <Button
        variant="subtle"
        size="large"
        disabled={isButtonDisabled}
        onClick={() => setIsConfirmationModalOpen(true)}
        startIcon={
          <Undo20Px
            fill={isButtonDisabled ? mainColours.text.disabled : "inherit"}
          />
        }
      >
        {t("get previous bin")}
      </Button>

      <ConfirmationModal
        modalTitle={t("previous bin")}
        modalText={confirmationModalText}
        isOpen={isConfirmationModalOpen}
        confirmCb={onConfirmGetPreviousBin}
        closeCb={() => setIsConfirmationModalOpen(false)}
        disableConfirmButton={isLoadingPreviousBin}
        labelledById="get-previous-bin-modal-title"
      />
    </>
  );
}

export default GetPreviousBinButton;
